/*
 * Meet Me There - custom wishlist
 * localStorage-backed, no app dependency. All events are delegated on
 * document so AJAX-rendered grids and theme-editor reloads keep working.
 *
 * - Logged-in customers get their own list (key namespaced by customer id);
 *   the guest list is merged into it once at login.
 * - Lists are shareable via /pages/wishlist?shared=<handles> and render
 *   read-only for visitors.
 */
(function () {
  'use strict';

  var BASE_KEY = 'mmt_wishlist_v1';
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var strings = (window.theme && window.theme.strings) || {};
  var customer = (window.theme && window.theme.customer) || null;
  var wishlistUrl = (window.theme && window.theme.wishlistUrl) || '/pages/wishlist';
  var STORAGE_KEY = customer && customer.id ? BASE_KEY + '_c' + customer.id : BASE_KEY;
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var toastTimer = null;
  var productCache = {};

  /* Lucide icons (ISC license) for JS-rendered elements */
  var ICONS = {
    heart: '<svg aria-hidden="true" focusable="false" role="presentation" class="icon-wishlist icon-wishlist-heart" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>',
    x: '<svg aria-hidden="true" focusable="false" role="presentation" class="icon-wishlist" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>',
    bag: '<svg aria-hidden="true" focusable="false" role="presentation" class="icon-wishlist" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>',
    check: '<svg aria-hidden="true" focusable="false" role="presentation" class="icon-wishlist" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>'
  };

  /* ------------------------------------------------------------------ *
   * Store
   * ------------------------------------------------------------------ */

  function readKey(key) {
    try {
      var raw = JSON.parse(window.localStorage.getItem(key));
      return Array.isArray(raw) ? raw.filter(function (item) { return item && item.handle; }) : [];
    } catch (e) {
      return [];
    }
  }

  function read() {
    return readKey(STORAGE_KEY);
  }

  function write(items) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      /* private mode / quota - wishlist just won't persist */
    }
    broadcast();
  }

  /* one-time merge of the guest list into the customer list at login */
  function mergeGuestList() {
    if (!customer || !customer.id) return;
    var guest = readKey(BASE_KEY);
    if (!guest.length) return;
    var own = read();
    var handles = own.map(function (item) { return item.handle; });
    guest.forEach(function (item) {
      if (handles.indexOf(item.handle) === -1) own.push(item);
    });
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(own));
      window.localStorage.removeItem(BASE_KEY);
    } catch (e) {
      /* noop */
    }
  }

  function has(handle) {
    return read().some(function (item) { return item.handle === handle; });
  }

  function add(handle, id) {
    var items = read();
    if (items.some(function (item) { return item.handle === handle; })) return;
    items.unshift({ handle: handle, id: id ? String(id) : null });
    write(items);
  }

  function remove(handle) {
    write(read().filter(function (item) { return item.handle !== handle; }));
  }

  function toggle(handle, id) {
    if (has(handle)) {
      remove(handle);
      return false;
    }
    add(handle, id);
    return true;
  }

  function clearAll() {
    write([]);
  }

  function broadcast() {
    document.dispatchEvent(new CustomEvent('theme:wishlist:change', { detail: { items: read() } }));
  }

  /* ------------------------------------------------------------------ *
   * Shared view helpers
   * ------------------------------------------------------------------ */

  function sharedHandles() {
    var params = new URLSearchParams(window.location.search);
    var shared = params.get('shared');
    if (!shared) return null;
    var handles = shared.split(',').filter(function (handle) {
      return /^[a-z0-9-]+$/.test(handle);
    });
    return handles.length ? handles.slice(0, 100) : null;
  }

  function sharedBy() {
    var params = new URLSearchParams(window.location.search);
    var by = params.get('by');
    return by ? by.slice(0, 40) : null;
  }

  function buildShareUrl() {
    var handles = read().map(function (item) { return item.handle; });
    var url = new URL(wishlistUrl, window.location.origin);
    url.searchParams.set('shared', handles.join(','));
    if (customer && customer.firstName) url.searchParams.set('by', customer.firstName);
    return url.toString();
  }

  /* ------------------------------------------------------------------ *
   * UI sync - buttons + header counters
   * ------------------------------------------------------------------ */

  function syncButtons(scope) {
    var buttons = (scope || document).querySelectorAll('[data-wishlist-toggle]');
    for (var i = 0; i < buttons.length; i++) {
      var button = buttons[i];
      var active = has(button.getAttribute('data-wishlist-handle'));
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      var label = active ? button.getAttribute('data-label-remove') : button.getAttribute('data-label-add');
      if (label) button.setAttribute('aria-label', label);
    }
  }

  function syncCounters() {
    var count = read().length;
    var counters = document.querySelectorAll('[data-wishlist-count]');
    for (var i = 0; i < counters.length; i++) {
      var counter = counters[i];
      var previous = counter.textContent;
      counter.textContent = count > 99 ? '99+' : String(count);
      counter.classList.toggle('is-visible', count > 0);
      if (count > 0 && previous !== counter.textContent) {
        counter.classList.remove('is-bumped');
        void counter.offsetWidth; /* restart animation */
        counter.classList.add('is-bumped');
      }
    }
  }

  function syncAll(scope) {
    syncButtons(scope);
    syncCounters();
  }

  /* ------------------------------------------------------------------ *
   * Micro-interactions
   * ------------------------------------------------------------------ */

  function burst(button) {
    if (reducedMotion) return;
    var old = button.querySelector('.wishlist-button__burst');
    if (old) old.remove();
    var holder = document.createElement('span');
    holder.className = 'wishlist-button__burst';
    holder.setAttribute('aria-hidden', 'true');
    for (var i = 0; i < 8; i++) {
      var particle = document.createElement('span');
      particle.className = 'wishlist-button__particle';
      particle.style.setProperty('--angle', (i * 45) + 'deg');
      holder.appendChild(particle);
    }
    button.appendChild(holder);
    window.setTimeout(function () { holder.remove(); }, 700);
  }

  function toast(message, showLink) {
    var element = document.querySelector('[data-wishlist-toast]');
    if (!element) {
      element = document.createElement('div');
      element.setAttribute('data-wishlist-toast', '');
      element.className = 'wishlist-toast';
      element.setAttribute('role', 'status');
      element.setAttribute('aria-live', 'polite');
      element.innerHTML =
        '<span class="wishlist-toast__icon">' + ICONS.heart + '</span>' +
        '<span class="wishlist-toast__text"></span>' +
        '<a class="wishlist-toast__link" href="' + wishlistUrl + '"></a>';
      document.body.appendChild(element);
    }
    element.querySelector('.wishlist-toast__text').textContent = message;
    var link = element.querySelector('.wishlist-toast__link');
    link.textContent = strings.wishlistView || 'View';
    link.style.display = showLink ? '' : 'none';
    element.classList.add('is-visible');
    if (toastTimer) window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(function () {
      element.classList.remove('is-visible');
    }, 2600);
  }

  /* ------------------------------------------------------------------ *
   * Cart integration
   * ------------------------------------------------------------------ */

  function addVariantToCart(variantId, button) {
    if (button) button.classList.add('is-loading');
    return window
      .fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      })
      .then(function (response) { return response.json(); })
      .then(function (data) {
        if (button) button.classList.remove('is-loading');
        if (data.status && data.status !== 200) {
          toast(data.description || data.message || 'Error', false);
          return;
        }
        if (window.cart && typeof window.cart.getCart === 'function') {
          /* refreshes the drawer contents and slides it open */
          window.cart.getCart();
        }
      })
      .catch(function () {
        if (button) button.classList.remove('is-loading');
      });
  }

  /* ------------------------------------------------------------------ *
   * Wishlist page rendering
   * ------------------------------------------------------------------ */

  function formatMoney(cents) {
    if (window.themeVendor && window.themeVendor.themeCurrency && window.theme && window.theme.moneyFormat) {
      return window.themeVendor.themeCurrency.formatMoney(cents, window.theme.moneyFormat);
    }
    return '€' + (cents / 100).toFixed(2).replace('.', ',');
  }

  function fetchProduct(handle, pruneMissing) {
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    return window
      .fetch(root + 'products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (response.status === 404 || response.status === 410) {
          if (pruneMissing) remove(handle); /* product deleted or hidden - prune silently */
          return null;
        }
        if (!response.ok) return null;
        return response.json();
      })
      .then(function (product) {
        if (product) productCache[handle] = product;
        return product;
      })
      .catch(function () { return null; });
  }

  function pageElements() {
    var wrapper = document.querySelector('[data-wishlist-page]');
    if (!wrapper) return null;
    return {
      wrapper: wrapper,
      grid: wrapper.querySelector('[data-wishlist-grid]'),
      empty: wrapper.querySelector('[data-wishlist-empty]'),
      count: wrapper.querySelector('[data-wishlist-page-count]'),
      toolbar: wrapper.querySelector('[data-wishlist-toolbar]'),
      sharedBanner: wrapper.querySelector('[data-wishlist-shared-banner]'),
      sharedByLabel: wrapper.querySelector('[data-wishlist-shared-by]')
    };
  }

  function pageString(elements, name) {
    return elements.grid.getAttribute('data-text-' + name) || '';
  }

  function updateCountLabel(elements, count) {
    if (!elements.count) return;
    var template = elements.count.getAttribute(count === 1 ? 'data-text-one' : 'data-text-other') || '';
    elements.count.textContent = template.replace('[[count]]', count);
    elements.count.classList.toggle('is-hidden', count === 0);
  }

  function cardTemplate(elements, product, index, options) {
    var image = product.images && product.images.length ? product.images[0] : null;
    var hoverImage = product.images && product.images.length > 1 ? product.images[1] : null;
    var onSale = product.compare_at_price && product.compare_at_price > product.price;
    var singleVariant = product.variants && product.variants.length === 1;
    var firstAvailable = null;
    if (product.variants) {
      for (var i = 0; i < product.variants.length; i++) {
        if (product.variants[i].available) {
          firstAvailable = product.variants[i];
          break;
        }
      }
    }

    var card = document.createElement('article');
    card.className = 'wishlist-card';
    card.setAttribute('data-wishlist-card', '');
    card.setAttribute('data-handle', product.handle);
    if (!reducedMotion) card.style.setProperty('--reveal-delay', Math.min(index * 55, 440) + 'ms');

    var atc = '';
    if (product.available) {
      if (singleVariant && firstAvailable) {
        atc =
          '<button type="button" class="wishlist-card__atc" data-wishlist-atc data-variant-id="' + firstAvailable.id + '">' +
          ICONS.bag + '<span>' + escapeHtml(pageString(elements, 'add-to-cart')) + '</span>' +
          '<em class="loader loader--line"><em class="loader-indeterminate"></em></em>' +
          '</button>';
      } else {
        atc =
          '<a class="wishlist-card__atc" href="' + product.url + '">' +
          ICONS.bag + '<span>' + escapeHtml(pageString(elements, 'choose-options')) + '</span>' +
          '</a>';
      }
    }

    var media =
      '<div class="wishlist-card__media-holder">' +
      '<a class="wishlist-card__media" href="' + product.url + '" aria-label="' + escapeHtml(product.title) + '">' +
      (image
        ? '<img class="wishlist-card__image" src="' + imageSize(image, 600) + '" srcset="' + imageSize(image, 400) + ' 400w, ' + imageSize(image, 600) + ' 600w, ' + imageSize(image, 900) + ' 900w" sizes="(min-width: 1024px) 25vw, 50vw" alt="' + escapeHtml(product.title) + '" loading="lazy">'
        : '<span class="wishlist-card__image wishlist-card__image--blank"></span>') +
      (hoverImage ? '<img class="wishlist-card__image wishlist-card__image--hover" src="' + imageSize(hoverImage, 600) + '" alt="" loading="lazy">' : '') +
      (!product.available ? '<span class="wishlist-card__badge">' + escapeHtml(pageString(elements, 'sold-out')) + '</span>' : '') +
      '</a>' +
      (options.shared
        ? '<button type="button" class="wishlist-button wishlist-button--card" data-wishlist-toggle data-wishlist-handle="' + product.handle + '" data-wishlist-id="' + product.id + '" data-label-add="' + escapeHtml(pageString(elements, 'save')) + '" data-label-remove="' + escapeHtml(pageString(elements, 'remove')) + '" aria-pressed="false" aria-label="' + escapeHtml(pageString(elements, 'save')) + '">' + ICONS.heart + '</button>'
        : '') +
      atc +
      '</div>';

    var price = product.price === 0
      ? '<span class="wishlist-card__price">' + (strings.free || 'Free') + '</span>'
      : '<span class="wishlist-card__price' + (onSale ? ' wishlist-card__price--sale' : '') + '">' + formatMoney(product.price) + '</span>' +
        (onSale ? '<s class="wishlist-card__price--compare">' + formatMoney(product.compare_at_price) + '</s>' : '');

    card.innerHTML =
      media +
      '<div class="wishlist-card__details">' +
      '<a class="wishlist-card__title" href="' + product.url + '">' + escapeHtml(product.title) + '</a>' +
      '<div class="wishlist-card__prices">' + price + '</div>' +
      '</div>' +
      (options.shared
        ? ''
        : '<button type="button" class="wishlist-card__remove" data-wishlist-remove data-wishlist-handle="' + product.handle + '" aria-label="' + escapeHtml(pageString(elements, 'remove')) + '">' + ICONS.x + '</button>');

    return card;
  }

  function imageSize(src, width) {
    return src.replace(/(\.[a-zA-Z]+)(\?.*)?$/, '_' + width + 'x$1$2');
  }

  function escapeHtml(value) {
    var div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  function renderSkeletons(grid, count) {
    grid.innerHTML = '';
    for (var i = 0; i < count; i++) {
      var skeleton = document.createElement('div');
      skeleton.className = 'wishlist-card wishlist-card--skeleton';
      skeleton.innerHTML = '<div class="wishlist-card__media-holder"><div class="wishlist-card__media"></div></div><div class="wishlist-card__details"><span class="wishlist-skeleton-line"></span><span class="wishlist-skeleton-line wishlist-skeleton-line--short"></span></div>';
      grid.appendChild(skeleton);
    }
  }

  function renderList(elements, handles, options) {
    if (!handles.length) {
      elements.grid.innerHTML = '';
      elements.grid.classList.add('is-hidden');
      if (elements.empty) elements.empty.classList.remove('is-hidden');
      if (elements.toolbar) elements.toolbar.classList.add('is-hidden');
      updateCountLabel(elements, 0);
      return;
    }

    elements.grid.classList.remove('is-hidden');
    if (elements.empty) elements.empty.classList.add('is-hidden');
    if (elements.toolbar) elements.toolbar.classList.toggle('is-hidden', !!options.shared);
    renderSkeletons(elements.grid, handles.length);

    Promise.all(
      handles.map(function (handle) { return fetchProduct(handle, !options.shared); })
    ).then(function (products) {
      elements.grid.innerHTML = '';
      var rendered = 0;
      products.forEach(function (product, index) {
        if (!product) return;
        elements.grid.appendChild(cardTemplate(elements, product, index, options));
        rendered++;
      });
      updateCountLabel(elements, rendered);
      if (!rendered) {
        elements.grid.classList.add('is-hidden');
        if (elements.toolbar) elements.toolbar.classList.add('is-hidden');
        if (elements.empty) elements.empty.classList.remove('is-hidden');
      }
      syncButtons(elements.grid);
    });
  }

  function renderPage() {
    var elements = pageElements();
    if (!elements || !elements.grid) return;

    var shared = sharedHandles();
    if (shared) {
      elements.wrapper.classList.add('wishlist-page--shared');
      if (elements.sharedBanner) {
        elements.sharedBanner.classList.remove('is-hidden');
        var by = sharedBy();
        if (by && elements.sharedByLabel) {
          var template = elements.sharedByLabel.getAttribute('data-text-by') || '';
          elements.sharedByLabel.textContent = template.replace('[[name]]', by);
        }
      }
      renderList(elements, shared, { shared: true });
      return;
    }

    renderList(elements, read().map(function (item) { return item.handle; }), { shared: false });
  }

  function removeCard(handle) {
    var card = document.querySelector('[data-wishlist-card][data-handle="' + handle + '"]');
    if (!card) return;
    var finish = function () {
      card.remove();
      afterRemoval();
    };
    if (reducedMotion) {
      finish();
      return;
    }
    card.classList.add('is-leaving');
    card.addEventListener('transitionend', finish, { once: true });
    window.setTimeout(finish, 450); /* safety net if transitionend never fires */
  }

  function afterRemoval() {
    var elements = pageElements();
    if (!elements || !elements.grid || elements.wrapper.classList.contains('wishlist-page--shared')) return;
    var left = elements.grid.querySelectorAll('[data-wishlist-card]').length;
    updateCountLabel(elements, left);
    if (!left) {
      elements.grid.classList.add('is-hidden');
      if (elements.toolbar) elements.toolbar.classList.add('is-hidden');
      if (elements.empty) elements.empty.classList.remove('is-hidden');
    }
  }

  /* ------------------------------------------------------------------ *
   * Share / save-all / clear
   * ------------------------------------------------------------------ */

  function share(button) {
    var url = buildShareUrl();
    var text = button.getAttribute('data-share-text') || '';
    if (window.navigator.share) {
      window.navigator.share({ title: document.title, text: text, url: url }).catch(function () {
        /* user dismissed the sheet - nothing to do */
      });
      return;
    }
    var copied = button.getAttribute('data-text-copied') || 'Link copied';
    var copy = function () {
      button.classList.add('is-done');
      window.setTimeout(function () { button.classList.remove('is-done'); }, 2000);
      toast(copied, false);
    };
    if (window.navigator.clipboard) {
      window.navigator.clipboard.writeText(url).then(copy);
    } else {
      window.prompt(copied, url);
    }
  }

  function saveShared() {
    var handles = sharedHandles() || [];
    handles
      .slice()
      .reverse()
      .forEach(function (handle) {
        var product = productCache[handle];
        if (!has(handle)) add(handle, product ? product.id : null);
      });
    toast(strings.wishlistAdded || 'Added to your wishlist', true);
  }

  /* ------------------------------------------------------------------ *
   * Events - everything delegated on document
   * ------------------------------------------------------------------ */

  document.addEventListener('click', function (event) {
    var toggleButton = event.target.closest('[data-wishlist-toggle]');
    if (toggleButton) {
      event.preventDefault();
      event.stopPropagation();
      var handle = toggleButton.getAttribute('data-wishlist-handle');
      if (!handle) return;
      var added = toggle(handle, toggleButton.getAttribute('data-wishlist-id'));
      var onSharedView = !!toggleButton.closest('.wishlist-page--shared');
      if (added) {
        burst(toggleButton);
        toast(strings.wishlistAdded || 'Added to wishlist', true);
      } else {
        toast(strings.wishlistRemoved || 'Removed from wishlist', false);
        if (!onSharedView && document.querySelector('[data-wishlist-grid]')) removeCard(handle);
      }
      return;
    }

    var removeButton = event.target.closest('[data-wishlist-remove]');
    if (removeButton) {
      event.preventDefault();
      var removeHandle = removeButton.getAttribute('data-wishlist-handle');
      remove(removeHandle);
      toast(strings.wishlistRemoved || 'Removed from wishlist', false);
      removeCard(removeHandle);
      return;
    }

    var atcButton = event.target.closest('[data-wishlist-atc][data-variant-id]');
    if (atcButton) {
      event.preventDefault();
      addVariantToCart(atcButton.getAttribute('data-variant-id'), atcButton);
      return;
    }

    var shareButton = event.target.closest('[data-wishlist-share]');
    if (shareButton) {
      event.preventDefault();
      share(shareButton);
      return;
    }

    var saveAllButton = event.target.closest('[data-wishlist-save-all]');
    if (saveAllButton) {
      event.preventDefault();
      saveShared();
      return;
    }

    var clearButton = event.target.closest('[data-wishlist-clear]');
    if (clearButton) {
      event.preventDefault();
      /* two-step confirm: first click arms the button, second click clears */
      if (!clearButton.classList.contains('is-armed')) {
        clearButton.classList.add('is-armed');
        window.setTimeout(function () { clearButton.classList.remove('is-armed'); }, 3500);
        return;
      }
      clearButton.classList.remove('is-armed');
      clearAll();
      renderPage();
      return;
    }
  });

  /* re-sync on wishlist mutations from this tab */
  document.addEventListener('theme:wishlist:change', function () {
    syncAll();
  });

  /* cross-tab sync */
  window.addEventListener('storage', function (event) {
    if (!event.key || event.key.indexOf(BASE_KEY) !== 0) return;
    syncAll();
    renderPage();
  });

  /* AJAX-rendered grids (filters, infinite scroll, quick view, tabs) */
  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches('[data-wishlist-toggle]')) {
          syncButtons(node.parentNode || document);
          return;
        }
        if (node.querySelector && node.querySelector('[data-wishlist-toggle]')) {
          syncButtons(node);
          return;
        }
      }
    }
  });

  function init() {
    mergeGuestList();
    syncAll();
    renderPage();
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* theme editor section re-renders */
  document.addEventListener('shopify:section:load', function () {
    syncAll();
    renderPage();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.MMTWishlist = {
    items: read,
    has: has,
    add: add,
    remove: remove,
    toggle: toggle,
    clear: clearAll,
    shareUrl: buildShareUrl,
    count: function () { return read().length; }
  };
})();
